import {useAuth} from '@apps-common/auth-react';
import DocumentHandler from '@apps-common/auth-utils/DocumentHandler.jsx';
import ErrorPage from '@apps-common/ui-components/ErrorPage.jsx';
import {MapAuthProvider} from '@apps-common/ui-maps/context/MapAuthProvider.jsx';
import DataCollectionsLayout from '@components/dataCollections/DataCollectionsLayout.tsx';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import {useColorScheme} from '@mui/material/styles';
import {
  ErrorBoundary as SentryErrorBoundary,
  setUser as setSentryUser,
  showReportDialog as showSentryReportDialog,
} from '@sentry/react';
import {clearCompareSubjects} from '@src/actions/compare.js';
import {identify, pageEvent} from '@src/analytics.js';
import ModalsManager from '@src/components/dialogs/index.jsx';
import Notifier from '@src/components/notifier/Notifier.tsx';
import LocalSidebar from '@src/components/sidebars/LocalSidebar.jsx';
import TopNavBar from '@src/components/TopNav.jsx';
import {IS_STAGING_AUTH, MAPBOX_ACCESS_TOKEN} from '@src/config.ts';
import {UI_MODES} from '@src/constants/map.ts';
import {
  ANALYZE_ROOT_ROUTE,
  DataCollectionsRoutePattern,
  isAnalyzeBuild,
} from '@src/constants/routes.ts';
import {useImpersonationAwareAuth} from '@src/hooks/dataCollections/impersonation/useImpersonationAwareAuth.ts';
import useMapMode from '@src/hooks/useMapMode.ts';
import usePlatformAccess from '@src/hooks/usePlatformAccess.ts';
import {useStore} from '@src/stores/useStore.ts';
import lazyRetry from '@src/utils/lazyRetry.ts';
import {useLDClient} from 'launchdarkly-react-client-sdk';
import PropTypes from 'prop-types';
import React, {Suspense, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {Navigate, Outlet, Route, Routes, useLocation} from 'react-router';
import Helm from './Helm.tsx';
import useAppSection from './hooks/useAppSection.js';
import {useFlags} from './hooks/useFlags.js';
import Overview from './routes/Overview.jsx';

const AddNewDestination = lazyRetry(
  () => import('./routes/AddNewDestination.jsx'),
);
const Analyze = lazyRetry(() => import('./routes/Analyze.tsx'));
const AreasOfInterest = lazyRetry(() => import('./routes/AreasOfInterest.jsx'));
const Collection = lazyRetry(() => import('./routes/DataCollection.jsx'));
const Collections = lazyRetry(() => import('./routes/DataCollections.jsx'));
const Destination = lazyRetry(() => import('./routes/Destination.jsx'));
const Destinations = lazyRetry(() => import('./routes/Destinations.jsx'));
const MapViewer = lazyRetry(() => import('./components/MapViewer.jsx'));
const Mosaics = lazyRetry(() => import('./routes/Mosaics.jsx'));
const NewCollection = lazyRetry(() => import('./routes/NewDataCollection.jsx'));
const Order = lazyRetry(() => import('./routes/Order.jsx'));
const Orders = lazyRetry(() => import('./routes/Orders.jsx'));
const SavedSearches = lazyRetry(() => import('./routes/SavedSearches.jsx'));
const Search = lazyRetry(() => import('./routes/Search.jsx'));
const Subscribe = lazyRetry(() => import('./routes/Subscribe.jsx'));
const Subscription = lazyRetry(() => import('./routes/Subscription.jsx'));
const Subscriptions = lazyRetry(() => import('./routes/Subscriptions.jsx'));

const SentryReportButton = ({eventId}) => {
  const {user} = useAuth();

  return (
    <Button
      color="primary"
      variant="contained"
      onClick={() => {
        showSentryReportDialog({
          eventId,
          user: {
            name: user?.claims?.name,
            email: user?.claims?.email,
          },
        });
      }}
    >
      Report issue
    </Button>
  );
};

SentryReportButton.propTypes = {
  eventId: PropTypes.string,
};

const App = () => {
  const auth = useAuth();
  const {pathname} = useLocation();
  const ldClient = useLDClient();
  const {setMode} = useColorScheme();
  const dispatch = useDispatch();
  const setMapDrawToolsVisible = useStore(
    state => state.map.setDrawToolsVisible,
  );
  const setMapUIMode = useStore(state => state.map.setUIMode);
  const resetResults = useStore(state => state.quickSearch.resetResults);
  const resetMosaics = useStore(state => state.mosaics.reset);
  const setPathname = useStore(state => state.app.setPathname);

  const {appSection, detailPage} = useAppSection();
  const {showVisualAnalysis, showMapRoutes} = useFlags();
  const {mapMode} = useMapMode();

  // Prefetch this so it's already in the cache when we need it
  // (and we don't need the result here so we ignore it)
  usePlatformAccess();

  const {
    impersonation: {isImpersonating},
    stopImpersonating,
  } = useImpersonationAwareAuth();

  useEffect(() => {
    if (!isImpersonating) {
      stopImpersonating();
    }
  }, [isImpersonating, stopImpersonating]);

  const [isIdentified, setIsIdentified] = useState(false);

  // Force dark mode
  useEffect(() => {
    setMode('dark');
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const {
      email,
      organization_id,
      organization_name,
      program_id,
      role_level,
      user_id,
      sh_user_id,
      pl_project,
      pl_workspace,
      pl_customer_account,
    } = auth.user?.claims ?? {};

    if (auth.user && !auth.pending && !auth.error) {
      setSentryUser({id: user_id, organization_id, program_id, email});

      identify(auth.user.claims);

      if (user_id && organization_id) {
        ldClient.identify(
          {
            kind: 'multi',
            user: {
              key: `user-${user_id}`,
              email,
              role_level,
              user_id,
              sh_user_id,
              pl_project,
              pl_workspace,
              _meta: {
                privateAttributes: ['email'],
              },
            },
            organization: {
              key: `organization-${organization_id}`,
              organization_id,
              organization_name,
              program_id,
              _meta: {
                privateAttributes: ['organization_name'],
              },
            },
            customer_account: pl_customer_account
              ? {
                  key: `customer-account-${pl_customer_account}`,
                  customer_account_id: pl_customer_account,
                }
              : undefined,
          },
          null,
          () => {
            setIsIdentified(true);
          },
        );
      }
    }
  }, [auth, ldClient]);

  useEffect(() => {
    if (pathname !== '/') {
      pageEvent({page: appSection, detailPage});
    }
  }, [pathname, appSection, detailPage]);

  useEffect(() => {
    dispatch(clearCompareSubjects());
    setMapUIMode(UI_MODES.NONE);
    resetMosaics();
    resetResults();
    setMapDrawToolsVisible(true);
  }, [
    dispatch,
    mapMode,
    resetResults,
    resetMosaics,
    setMapUIMode,
    setMapDrawToolsVisible,
  ]);

  // Save the current path in the global store so we can use it even inside
  // other contexts/fibers (which in this app means the Map component),
  // because inside those other contexts, the router context gets clobbered
  // and useLocation is unreliable.
  useEffect(() => {
    setPathname(pathname);
  }, [pathname, setPathname]);

  return (
    <>
      <MapAuthProvider
        mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
        authToken={auth.user?.accessToken}
        authTarget={IS_STAGING_AUTH ? 'staging' : 'prod'}
      >
        <SentryErrorBoundary
          fallback={({error, eventId}) => {
            const showRetry =
              error.name === 'ChunkLoadError' ||
              error.message.includes(
                'Failed to fetch dynamically imported module',
              ) ||
              // Firefox and Safari have a slightly different error message, apparently
              error.message.includes('dynamically imported module');

            const retryProps = showRetry
              ? {
                  message:
                    'Try refreshing the page or let us know what happened.',
                  onRetry: () => window.location.reload(),
                }
              : {};

            return (
              <ErrorPage
                reportIssueButton={<SentryReportButton eventId={eventId} />}
                {...retryProps}
              />
            );
          }}
          showDialog
          dialogOptions={{
            user: {
              name: auth.user?.claims.name,
              email: auth.user?.claims.email,
            },
          }}
        >
          <TopNavBar />

          <DocumentHandler>
            {isIdentified && (
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                }}
              >
                <ModalsManager />

                {isAnalyzeBuild ? (
                  <Routes>
                    <Route index element={<Navigate to="browse" replace />} />
                    <Route element={<MapViewer />}>
                      <Route
                        path="*"
                        element={
                          <Helm title="Browser">
                            <Suspense>
                              <Analyze />
                            </Suspense>
                          </Helm>
                        }
                      />
                    </Route>
                  </Routes>
                ) : (
                  <Routes>
                    <Route
                      element={
                        <>
                          <LocalSidebar />
                          <Outlet />
                        </>
                      }
                    >
                      <Route index element={<Navigate to="overview" />} />

                      <Route
                        path="overview"
                        element={
                          <Helm title="Overview">
                            <Overview />
                          </Helm>
                        }
                      />

                      <Route
                        path={DataCollectionsRoutePattern.root}
                        element={<DataCollectionsLayout />}
                      >
                        <Route
                          index
                          element={
                            <Helm title="Data Collections">
                              <Suspense>
                                <Collections />
                              </Suspense>
                            </Helm>
                          }
                        />

                        <Route
                          path={DataCollectionsRoutePattern.new}
                          element={<NewCollection />}
                        />

                        <Route
                          path={DataCollectionsRoutePattern.collection.detail}
                          element={<Collection />}
                        />
                        <Route
                          path={DataCollectionsRoutePattern.collection.tiles}
                          element={<Collection />}
                        />
                        <Route
                          path={
                            DataCollectionsRoutePattern.collection
                              .configurations
                          }
                          element={<Collection />}
                        />
                        <Route
                          path={DataCollectionsRoutePattern.collection.arrays}
                          element={<Collection />}
                        />
                      </Route>

                      <Route path="subscriptions">
                        <Route
                          index
                          element={
                            <Helm title="Subscriptions">
                              <Suspense>
                                <Subscriptions />
                              </Suspense>
                            </Helm>
                          }
                        />

                        <Route
                          path=":subscriptionId"
                          element={<Subscription />}
                        />
                      </Route>

                      <Route path="orders">
                        <Route
                          index
                          element={
                            <Helm title="Orders">
                              <Suspense>
                                <Orders />
                              </Suspense>
                            </Helm>
                          }
                        />

                        <Route path=":orderId" element={<Order />} />
                      </Route>

                      <Route path="destinations">
                        <Route
                          index
                          element={
                            <Helm title="Destinations">
                              <Suspense>
                                <Destinations />
                              </Suspense>
                            </Helm>
                          }
                        />

                        <Route
                          path="add"
                          element={<Navigate to="/destinations/new" />}
                        />

                        <Route path="new" element={<AddNewDestination />} />

                        <Route
                          path=":destinationId"
                          element={<Destination />}
                        />
                      </Route>
                    </Route>

                    <Route
                      element={
                        <Suspense>
                          <MapViewer />
                        </Suspense>
                      }
                    >
                      <Route
                        path="subscriptions/new"
                        element={
                          <Helm title="Subscribe to Data">
                            <Suspense>
                              <Subscribe />
                            </Suspense>
                          </Helm>
                        }
                      />

                      <Route
                        path="mosaics"
                        element={
                          <Helm title="Mosaics">
                            <Suspense>
                              <Mosaics />
                            </Suspense>
                          </Helm>
                        }
                      />

                      {showMapRoutes && (
                        <Route
                          path="explorer"
                          element={
                            <Helm title="Daily results">
                              <Suspense>
                                <Search />
                              </Suspense>
                            </Helm>
                          }
                        />
                      )}

                      {showMapRoutes && (
                        <Route
                          path="aois"
                          element={
                            <Helm title="Areas of interest">
                              <Suspense>
                                <AreasOfInterest />
                              </Suspense>
                            </Helm>
                          }
                        />
                      )}

                      {showMapRoutes && (
                        <Route
                          path="saved-searches"
                          element={
                            <Helm title="Saved Searches">
                              <Suspense>
                                <SavedSearches />
                              </Suspense>
                            </Helm>
                          }
                        />
                      )}

                      {showVisualAnalysis && (
                        <Route
                          path={`${ANALYZE_ROOT_ROUTE}/*`}
                          element={
                            <Helm title="Visual Analysis">
                              <Suspense>
                                <Analyze />
                              </Suspense>
                            </Helm>
                          }
                        />
                      )}
                    </Route>
                    <Route
                      path="/"
                      element={<Navigate to={import.meta.BASE_PATH} />}
                    />
                    <Route
                      path="*"
                      element={
                        <ErrorPage
                          message="the page was not found"
                          statusCode={404}
                        />
                      }
                    />
                  </Routes>
                )}
              </Box>
            )}
          </DocumentHandler>

          <Notifier />
        </SentryErrorBoundary>
      </MapAuthProvider>
    </>
  );
};

export default App;
